import { RGBA_ASTC_10x10_Format } from "three";
import { IJSONSchema } from "../../../../base/common/jsonSchema";
import { safeStringify } from "../../../../base/common/objects";
import { IHierarchyService } from "../../../hierarchy/common/hierarchy";
import { ILogService } from "../../../log/common/log";
import { IPropertyService, PropertyScope, PropertyViewMode } from "../../../properties/common/properties";
import { AIToolCallResponse, FunctionAiTool, IAITool } from "../ai";

// #region Find Properties Tool

type FindPropertiesParams = {
    nodesIds?: string[];
    nodeQuery?: string;
    nodeType?: string;
    nodeMaxResults?: number;
    propertyQuery?: string;
    propertyType?: string;
    includeValues?: boolean;
    maxValueSize?: number;
    searchInDescriptions?: boolean;
    maxResults?: number;
    offset?: number;
}


export class FindPropertiesTool implements IAITool {
    readonly name = "property_find";
    readonly description = "Find property information of hierarchy nodes/objects in the scene. Supports two modes: (1) Direct query by node IDs, or (2) Search for nodes first, then query their properties. Both propertyQuery and propertyType support regex patterns for flexible matching (case-insensitive).";
    readonly type = "function";
    readonly parametersJSONSchema: IJSONSchema = {
        type: "object",
        properties: {
            nodesIds: {
                type: "array",
                description: "Array of node IDs to get properties for. Use this when you already know the node IDs.",
                items: {
                    type: "string",
                },
            },
            nodeQuery: {
                type: "string",
                description: "Search query to find nodes first (e.g., 'player' to find nodes with 'player' in their name). Use this when you need to search for nodes by name. Multiple terms can be separated by commas.",
            },
            nodeType: {
                type: "string",
                description: "Filter nodes by type when using nodeQuery. Supports regex patterns (e.g., 'Mesh', 'Light.*', 'Mesh|Camera'). Only used with nodeQuery.",
            },
            nodeMaxResults: {
                type: "number",
                description: "Maximum number of nodes to find when using nodeQuery. Default is 20. Only used with nodeQuery.",
                default: 20,
            },
            propertyQuery: {
                type: "string",
                description: "Optional query string to filter properties (e.g., 'color' to find properties related to color). This string will be interpreted as a regular expression.",
            },
            propertyType: {
                type: "string",
                description: "Optional filter by property type. Supports regex patterns for flexible matching. Examples: 'number' (exact match), 'vector.*' (matches vector2, vector3, etc.), 'color|material' (matches either). Case-insensitive.",
            },
            includeValues: {
                type: "boolean",
                description: "Whether to include the actual property values in the RESPONSE. Default is false. Set to true to see current values in results. WARNING: Enabling this may return large amounts of data if properties have large values (e.g., geometry data), which can lead to slower responses and overwhelming outputs. Use with specific property filters to limit results when enabling or consider setting maxValueSize to limit the size of included values.",
                default: false,
            },
            maxValueSize: {
                type: "number",
                description: "When includeValues is true, this limits the maximum length of stringified property values in the response to avoid overwhelming outputs. Default is 200 characters. Longer values will be truncated with an indication.",
                default: 200,
            },
            searchInDescriptions: {
                type: "boolean",
                description: "Whether to SEARCH within property descriptions/tooltips in addition to keys and labels. Default is false. Enable for more thorough semantic search, but may be slower and return more results.",
                default: false,
            },
            maxResults: {
                type: "number",
                description: "Maximum number of properties to return per node. Default is 50. Use lower values for faster responses.",
                default: 50,
            },
            offset: {
                type: "number",
                description: "Number of matching properties to skip before returning results (for pagination). Default is 0.",
                default: 0,
            },
        },
        required: [],
    }

    constructor(
        @IHierarchyService private readonly hierarchy: IHierarchyService,
        @IPropertyService private readonly propertyService: IPropertyService,
        @ILogService private readonly logger: ILogService,
    ) { }


    async call(parameters?: FindPropertiesParams): Promise<AIToolCallResponse> {

        // Validate parameters
        if (!parameters?.nodesIds && !parameters?.nodeQuery) {
            return {
                result: {
                    message: "Either 'nodesIds' or 'nodeQuery' must be provided.",
                    success: false,
                }
            }
        }

        if (!this.hierarchy) {
            (this.logger || console).error("Hierarchy service not available.");
            return {
                result: {
                    message: "No project or scene hierarchy available.",
                    success: false,
                }
            }
        }

        // Determine which nodes to query
        let nodeIds: string[] | null = null;
        if (parameters.nodesIds?.length) {
            nodeIds = parameters.nodesIds;
        }

        if (parameters.nodeQuery) {
            // Mode 2: Search for nodes first
            const query = parameters.nodeQuery.split(',').map(q => q.trim()).filter(q => q.length > 0);
            const nodeMaxResults = parameters.nodeMaxResults ?? 20;
            const nodeTypeRegex = parameters.nodeType ? new RegExp(parameters.nodeType, "i") : null;

            // Search with higher limit if filtering by type
            const searchLimit = nodeTypeRegex ? nodeMaxResults * 3 : nodeMaxResults;
            let foundNodes = await this.hierarchy.search(query, searchLimit) ?? [];

            // Apply type filter
            if (nodeTypeRegex) {
                foundNodes = foundNodes.filter(n => n.type && nodeTypeRegex.test(n.type));
            }

            // Limit to nodeMaxResults
            foundNodes = foundNodes.slice(0, nodeMaxResults);

            if (foundNodes.length === 0 && (!nodeIds || nodeIds.length <= 0)) {
                return {
                    result: {
                        aiMessage: safeStringify({
                            results: [],
                            totalReturned: 0,
                            totalMatched: 0,
                            hasMore: false,
                            message: `No nodes found matching query '${parameters.nodeQuery}'`,
                        }),
                        success: true,
                    }
                }
            }

            // add node ids to list if they were found
            nodeIds ||= [];
            for (const n of foundNodes) {
                nodeIds.push(n.id);
            }
            // make unique
            nodeIds = Array.from(new Set(nodeIds));
        }

        if (!nodeIds || nodeIds?.length === 0) {
            throw new Error("No node IDs provided or found.");
        }

        const regex = parameters.propertyQuery ? new RegExp(parameters.propertyQuery, "i") : null;
        const typeRegex = parameters.propertyType ? new RegExp(parameters.propertyType, "i") : null;
        const includeValues = parameters.includeValues ?? false;
        const searchInDescriptions = parameters.searchInDescriptions ?? false;
        const maxResults = parameters.maxResults ?? 50;
        const offset = parameters.offset ?? 0;
        const maxValueSize = Math.max(10, parameters.maxValueSize ?? 200);

        const results: unknown[] = [];
        let totalFound = 0;
        let totalMatched = 0;

        for (const nodeId of nodeIds) {
            const node = this.hierarchy.findById(nodeId);
            if (!node) {
                results.push({
                    nodeId,
                    error: "Node not found"
                });
                continue;
            }

            const nodeResult = {
                nodeId: node.id,
                nodeName: node.name,
                nodeType: node.type,
                properties: new Array(),
            };

            const props = await this.propertyService.getProperties(node, { scope: PropertyScope.AI_QUERY, viewMode: PropertyViewMode.ALL });
            if (props) {
                let propertyCount = 0;
                let skipped = 0;
                for (const group of props.groups) {
                    for (const p of group.properties) {
                        // Apply text filter (key, label, and optionally description/tooltip)
                        if (regex) {
                            let matches = regex.test(p.label) || regex.test(p.key);

                            // Also search in description/tooltip if requested
                            if (!matches && searchInDescriptions) {
                                const tooltip = p.getTooltip ? p.getTooltip() : null;
                                let description = p.metadata?.description;
                                if (typeof description === "string") description = description;
                                else description = description?.value;
                                if (tooltip && regex.test(tooltip)) {
                                    matches = true;
                                } else if (description && regex.test(description)) {
                                    matches = true;
                                }
                            }

                            if (!matches) {
                                continue;
                            }
                        }

                        // Apply type filter
                        if (typeRegex && p.type && !typeRegex.test(p.type)) {
                            continue;
                        }

                        totalMatched++;

                        // Skip if within offset range
                        if (skipped < offset) {
                            skipped++;
                            continue;
                        }

                        if (propertyCount >= maxResults) {
                            break;
                        }

                        const propData: any = {
                            propertyPath: p.metadata?.address || p.key,
                            propertyKey: p.key,
                            propertyLabel: p.label,
                            propertyType: p.type,
                            groupName: group.name,
                        };

                        if (includeValues) {
                            propData.value = p.value;
                            if (maxValueSize && maxValueSize > 0) {
                                try {
                                    const stringifiedValue = safeStringify(p.value, undefined, safeStringifyReplacer);
                                    if (stringifiedValue.length > maxValueSize) {
                                        propData.value = stringifiedValue.slice(0, maxValueSize) + `... (truncated, total length: ${stringifiedValue.length} chars)`;
                                    }
                                } catch {
                                    // toJSON() or other serialization error — provide a safe fallback
                                    propData.value = `[Unserializable: ${typeof p.value}]`;
                                }
                            }
                        }

                        nodeResult.properties.push(propData);
                        propertyCount++;
                        totalFound++;
                    }
                    if (propertyCount >= maxResults) {
                        break;
                    }
                }
            }

            results.push(nodeResult);
        }

        const responseData: any = {
            totalReturned: totalFound,
            totalMatched: totalMatched,
            hasMore: totalMatched > (offset + totalFound),
            pagination: {
                offset,
                limit: maxResults
            },
            filters: {
                query: parameters.propertyQuery,
                type: parameters.propertyType,
                includeValues,
                searchInDescriptions
            },
            results,
        };

        // Include node search info if nodeQuery was used
        if (parameters.nodeQuery) {
            responseData.nodeSearch = {
                query: parameters.nodeQuery,
                nodeType: parameters.nodeType,
                nodesFound: nodeIds.length
            };
        }

        let aiMessage: string;
        try {
            aiMessage = safeStringify(responseData, undefined, safeStringifyReplacer);
        } catch {
            // Fallback: strip values that may contain unserializable objects
            for (const r of results as any[]) {
                if (r.properties) {
                    for (const p of r.properties) {
                        if (p.value !== undefined && typeof p.value === 'object') {
                            p.value = `[Unserializable: ${p.value?.constructor?.name || 'object'}]`;
                        }
                    }
                }
            }
            aiMessage = safeStringify(responseData, undefined, safeStringifyReplacer);
        }

        return {
            result: {
                message: responseData,
                aiMessage,
                success: true,
            }
        }
    }
}


// #region Read Property Tool

export interface ReadPropertyParameters {
    nodeId: string;
    propertyPath: string;
    value?: boolean;
    metadata?: boolean;
}

export class ReadPropertyTool implements FunctionAiTool {

    readonly name = "property_read";
    readonly description = "Read the value or metadata of a specific property of a hierarchy node/object in the scene by its ID.";
    readonly type = "function";
    readonly parametersJSONSchema: IJSONSchema = {
        type: "object",
        properties: {
            nodeId: {
                type: "string",
                description: "ID of the node to get the property from.",
            },
            propertyPath: {
                type: "string",
                description: "Path of the property to read (e.g., 'transform.position.x' or 'materials[0].color').",
            },
            value: {
                type: "boolean",
                description: "Whether to include the property value in the response. Default is true.",
                default: true,
            },
            metadata: {
                type: "boolean",
                description: "Whether to include the property metadata in the response. The metadata includes UI settings, description/tooltip about the property and sometimes available options. Default is false.",
                default: false,
            },
        },
        required: ["nodeId", "propertyPath"],
    }

    constructor(
        @IHierarchyService private readonly hierarchy: IHierarchyService,
        @IPropertyService private readonly propertyService: IPropertyService,
        @ILogService private readonly logger: ILogService,
    ) { }

    async call(parameters?: ReadPropertyParameters): Promise<AIToolCallResponse> {
        if (!parameters?.nodeId || !parameters?.propertyPath) {
            return {
                result: {
                    message: "Node ID or property key not provided.",
                    success: false,
                }
            }
        }

        if (!this.hierarchy) {
            (this.logger || console).error("Hierarchy service not available.");
            return {
                result: {
                    message: "No project or scene hierarchy available.",
                    success: false,
                }
            }
        }

        const node = this.hierarchy.findById(parameters.nodeId);
        if (!node) {
            return {
                result: {
                    message: `Node with ID '${parameters.nodeId}' not found.`,
                    aiMessage: safeStringify({
                        error: "Node not found",
                        nodeId: parameters.nodeId,
                    }),
                    success: false,
                }
            }
        }

        const propValue = await this.propertyService.findProperty({
            objectId: node.id,
            propertyPath: parameters.propertyPath,
        })

        if (!propValue) {
            return {
                result: {
                    aiMessage: safeStringify({
                        error: "Property not found",
                        nodeId: parameters.nodeId,
                        propertyPath: parameters.propertyPath
                    }),
                    message: `Property '${parameters.propertyPath}' not found on node '${node.name}' (ID: ${node.id})`,
                    success: false,
                }
            }
        }

        const result: any = {
            nodeId: node.id,
            nodeName: node.name,
            nodeType: node.type,
            propertyPath: parameters.propertyPath,
            propertyKey: propValue.key,
            propertyLabel: propValue.label,
            propertyType: propValue.type,
            isEditable: propValue.editable,
        };

        if (parameters.value !== false) {
            result.value = propValue.value;
        }

        if (parameters.metadata) {
            result.metadata = propValue.metadata;
            result.tooltip = propValue.getTooltip ? propValue.getTooltip() : null;
        }

        return {
            result: {
                message: result,
                aiMessage: safeStringify(result, undefined, safeStringifyReplacer),
                success: true,
            }
        }
    }
}


function safeStringifyReplacer(key: string, value: any): any {
    // We don't want to include large arrays (e.g., geometry data) in the stringified output. This includes binary data represented as Uint8Array, Uint16Array, Float32Array, etc.
    const res = getLargeArrayInfo(value);
    if (res) {
        return res;
    }
    return value;
}


interface LargeArrayLike {
    type: string;
    contentType?: 'binary' | 'array';
    totalLength: number;
    atStart?: Array<any>;
    atEnd?: Array<any>;
}

function getLargeArrayInfo(value: any): string | LargeArrayLike | null {

    if (value && typeof value === 'object') {
        try {
            const constructorName = value.constructor?.name;
            if (['Uint8Array', 'Uint16Array', 'Uint32Array', 'Int8Array', 'Int16Array', 'Int32Array', 'Float32Array', 'Float64Array'].includes(constructorName)) {
                if (value.length > 30) {
                    let atStart = undefined;
                    let atEnd = undefined;
                    let length = value.length;
                    if (isTypedArrayLike(value)) {
                        atStart = Array.from(value.slice(0, 10));
                        atEnd = Array.from(value.slice(-10));
                        return {
                            type: constructorName,
                            contentType: 'array',
                            totalLength: length,
                            atStart,
                            atEnd,
                        };
                    } else if (isArrayBufferLike(value)) {
                        length = value.byteLength;
                        const view = new Uint8Array(value);
                        atStart = Array.from(view.slice(0, 10));
                        atEnd = Array.from(view.slice(-10));
                        return {
                            type: constructorName,
                            contentType: 'binary',
                            totalLength: length,
                            atStart,
                            atEnd,
                        };
                    }
                    else {
                        return {
                            type: constructorName,
                            totalLength: length,
                        };
                    }
                }
            }
            if (Array.isArray(value)) {
                if (value.length > 30) {
                    return {
                        type: 'Array',
                        totalLength: value.length,
                        atStart: value.slice(0, 10),
                        atEnd: value.slice(-10),
                    };
                }
            }
        }
        catch (e) {
            setTimeout(() => {
                console.warn("[Needle] Error in safeStringifyReplacer while checking for large array-like object - throwing delayed. Please report an issue.");
                throw e; // throw async to send to backend logging
            }, 3000);
            return "Error checking large array-like object";
        }
    }


    return null;
}

function isArrayBufferLike(value: any): boolean {
    return value && typeof value === 'object' && typeof value.byteLength === 'number' && typeof value.slice === 'function';
}
function isTypedArrayLike(value: any): boolean {
    return value && typeof value === 'object' && typeof value.length === 'number' && isArrayBufferLike(value.buffer);
}